import { useMessageContext } from 'stream-chat-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useGetChatCredentials } from '@/hooks/chat/useGetChatCredentials';
import { customReactionOptions } from './custom-reaction-options';

interface ReactionGroup {
  count: number;
  firstReactionDate: string;
}

export const CustomReactionList = () => {
  const { message, handleReaction } = useMessageContext('useProcessReactions');
  const { data } = useGetChatCredentials();

  const renderIcon = (type: string) => {
    const option = customReactionOptions.find((o) => o.type === type);
    if (option) {
      const { Component } = option;
      return (
        <div className="text-xs">
          <Component />
        </div>
      );
    }

    return <></>;
  };

  const groupedReactions = (message.latest_reactions ?? []).reduce(
    (acc, reaction) => {
      const type = reaction.type;
      if (!acc[type]) {
        acc[type] = {
          count: 0,
          firstReactionDate: reaction.created_at,
        };
      }

      acc[type].count += 1;
      // keep the earliest date for this reaction type
      // we want to sort on these
      if (new Date(reaction.created_at) < new Date(acc[type].firstReactionDate)) {
        acc[type].firstReactionDate = reaction.created_at;
      }

      return acc;
    },
    {} as Record<string, ReactionGroup>
  );

  const ownReactions = (message.own_reactions ?? []).reduce(
    (acc, reaction) => {
      acc[reaction.type] = (acc[reaction.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const getStyle = (isOwnReaction: boolean) => {
    if (isOwnReaction) {
      return 'bg-blue-100 dark:bg-blue-900 border border-primary/50';
    }
    return 'bg-accent dark:bg-slate-800 border';
  };

  const sortedReactions = Object.entries(groupedReactions).sort(([, groupA], [, groupB]) => {
    return (
      new Date(groupA.firstReactionDate).getTime() - new Date(groupB.firstReactionDate).getTime()
    );
  });

  if (!data) return null;

  return (
    <div
      className={`flex flex-row gap-1 pb-px ${message.user!.id == data!.userId! ? 'justify-end' : 'justify-start'}`}
    >
      {sortedReactions.map(([type, { count }]) => {
        const isOwnReaction = ownReactions[type] > 0;

        const reactedBy = (message.latest_reactions ?? [])
          .filter((r) => r.type === type && r.user_id !== data!.userId)
          .map((r) => r.user!.name!);

        return (
          <TooltipProvider key={type}>
            <Tooltip delayDuration={100}>
              <TooltipTrigger asChild>
                <div
                  onClick={(e) => {
                    handleReaction(type, e);
                  }}
                  className={`${getStyle(isOwnReaction)} cursor-pointer flex items-center gap-1 p-1 rounded-lg`}
                >
                  {renderIcon(type)}
                  <p className="text-xs text-foreground">{count}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {[...reactedBy, ...(isOwnReaction ? ['You (click to remove)'] : [])].join(', ')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}
    </div>
  );
};
